import { enableStaticRendering } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { ReactNode, createContext, useEffect } from 'react';
import { ENTER_STORE_LOCAL_STORAGE_KEY } from 'stores/EnterStore';
import { RootStore, RootStoreHydration } from 'stores/RootStore';

enableStaticRendering(typeof window === 'undefined');

let store: RootStore;
export const RootContext = createContext<RootStore | undefined>(undefined);

export function RootStoreProvider({
  children,
  hydrationData,
}: {
  children: ReactNode;
  hydrationData?: RootStoreHydration;
}) {
  const store = initializeStore(hydrationData);
  const router = useRouter();

  useEffect(() => {
    // Stores must be loaded within a useEffect, as otherwise there are differences between the server-side rendered
    // contents of the store and what the client loads and then renders
    store.loadEnterStoreLocalStorage(
      store.enterStore,
      ENTER_STORE_LOCAL_STORAGE_KEY,
    );
    store.enterStore.initReactions();
    store.initialized = true;
  }, []);

  useEffect(() => {
    if (router.isReady) {
      store.enterStore.setRouter(router);
    }
  }, [router.isReady]);

  return <RootContext.Provider value={store}>{children}</RootContext.Provider>;
}

function initializeStore(initialData?: RootStoreHydration): RootStore {
  const _store = store ?? new RootStore();

  if (initialData) {
    _store.hydrate(initialData);
  }
  // For SSG and SSR always create a new store
  if (typeof window === 'undefined') return _store;
  // Create the store once in the client
  if (!store) store = _store;

  return _store;
}
